import React, { ReactElement, useEffect } from "react";
import { BreadcrumbItem, ICustomDomain } from "@genericTypes/sharedTypes";
import DefaultLayout from "@layout/default";
import SideWidget from "@components/shared/sideWidget";
import MainContent from "@components/blog";
import { Blog } from "@genericTypes/sharedTypes";
import { useQuery } from "@tanstack/react-query";
import { GQLCategories, GQLPosts } from "@gql/types/gql";
import MetaTags from "@components/shared/metaTags";
import { translate } from "@helpers/translationObjectFormatter";
import { TranslationsObject } from "@genericTypes/sharedTypes";
import { extractExcerptFromBody } from "@lib/sharedUtils";
import { getMainTitlesNavigationFromBody } from "@lib/sharedUtils";
import Breadcrumb from "@components/shared/breadcrumb";
import initialCall from "@helpers/initial-call";

export interface Props {
    blog: Blog;
}

import { GetServerSidePropsContext } from "next";
import { activePageAtom } from "@store/generalStore";
import { useSetAtom } from "jotai";
const Page = function CategoryPage({
    blog,
    title,
}: {
    blog: GQLPosts;
    title: string;
}) {
    const { data: allCategories } = useQuery<GQLCategories[], Error>({
        queryKey: ["allCategories"],
    });
    const blogPostCategory = allCategories?.find(
        (category) => category.id === blog?.categoryId,
    );

    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const setActivePage = useSetAtom(activePageAtom);
    useEffect(() => {
        setActivePage("blog");
    }, []);

    const cashedArrayOfRecentPosts = blog?.categoryId
        ? ["recentPostsByCategory", blogPostCategory?.slug]
        : ["recentPosts"];

    const { data: recentPosts } = useQuery<GQLPosts, Error>(
        cashedArrayOfRecentPosts,
    );

    const myList = [
        {
            title: translate(translations, "several_general_contents"),
            slug: "contents",
            content: getMainTitlesNavigationFromBody(blog?.body as string),
        },

        {
            title: translate(translations, "several_general_recentPosts"),
            slug: "recent posts",
            content: recentPosts as { title: string; link: string }[],
        },
    ];

    let breadcrumbArr;
    if (blogPostCategory) {
        breadcrumbArr = [
            {
                label: translate(
                    translations,
                    "several_general_breadcrumb_home",
                ),
                link: "/",
                isActive: true,
                showOnMobile: true,
            },
            {
                label: blogPostCategory?.label ?? blogPostCategory?.name,
                link: `/${blogPostCategory?.slug as string}`,
                isActive: true,
                showOnMobile: true,
            },

            {
                label: `${blog?.title as string}`,
                link: "#",
                isActive: false,
                showOnMobile: false,
            },
        ];
    } else {
        breadcrumbArr = [
            {
                label: translate(
                    translations,
                    "several_general_breadcrumb_home",
                ),
                link: "/",
                isActive: true,
                showOnMobile: true,
            },
            {
                label: translate(translations, "several_general_blogWord"),
                link: "/blog",
                isActive: true,
                showOnMobile: true,
            },

            {
                label: `${blog?.title as string}`,
                link: "#",
                isActive: false,
                showOnMobile: false,
            },
        ];
    }
    const getDescription = (fallback: string) => {
        for (let i = 0; i < blog?.metatags!.length; i++) {
            if (
                blog?.metatags![i]?.key === "description" &&
                blog?.metatags[i]?.value
            ) {
                return blog?.metatags[i]?.value;
            }
        }
        if (blog?.excerpt) {
            return blog.excerpt;
        }
        if (blog?.body) {
            return extractExcerptFromBody(blog.body, 160);
        }

        return fallback;
    };
    const { data: domainInfo } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });

    const getKeyWords = () => {
        for (let i = 0; i < blog?.metatags!.length; i++) {
            if (
                blog?.metatags![i]?.key === "keywords" &&
                blog?.metatags[i]?.value
            ) {
                return blog?.metatags[i]?.value;
            }
        }

        if (blog?.tags) {
            return blog?.tags?.map((tag) => tag?.name).join(",");
        }
        for (let i = 0; i < (domainInfo?.domain?.metatags!.length ?? 0); i++) {
            if (
                domainInfo?.domain?.metatags![i]?.key === "keywords" &&
                domainInfo?.domain?.metatags[i]?.value
            ) {
                return domainInfo?.domain?.metatags[i]?.value;
            }
        }

        return "vpn , auto warranty";
    };

    return (
        <>
            <MetaTags
                title={`${
                    title ? title : (blog?.title as string)
                } | Several.com`}
                description={
                    getDescription(
                        translate(
                            translations,
                            "several_general_meta_description",
                        ),
                    ) as string
                }
                keyWords={getKeyWords() as string}
                image={blog?.image?.fullPath as string}
                url={blog?.url?.url as string}
                type="article"
                noIndex
            />
            <section className="max-w-7xl mx-auto px-5">
                <Breadcrumb arr={breadcrumbArr as BreadcrumbItem[]} />
                <div className=" flex flex-col-reverse lg:flex-row lg:items-start items-center lg:my-16 mb-9">
                    <SideWidget
                        contentToBeSharedUrl={`${blog?.url?.url as string}`}
                        myList={myList}
                        category={blogPostCategory as GQLCategories}
                    />
                    <MainContent
                        blog={blog}
                        category={blogPostCategory}
                        listOfContentsMobile={myList[0].content}
                        recentPostsMobile={myList[1].content}
                    />
                </div>
            </section>
        </>
    );
};

Page.getLayout = function getLayout(page: ReactElement) {
    return <DefaultLayout pageType="blogId">{page}</DefaultLayout>;
};

// export function getStaticPaths() {
//     return initialCallPaths();
// }
export async function getServerSideProps(ctx: GetServerSidePropsContext) {
    return await initialCall("Preview", ctx);
}

export default Page;
